import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PlayCircle, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPost } from '../../lib/api.js';
import { usePowerDial, usePowerDialDefaults } from '../../state/powerDial.js';

export const RESUMABLE_QUERY_KEY = ['powerdial-resumable'];

/**
 * Bottom chip offering to continue a Power Dial session that was ended early.
 * Resume opens the normal Power Dial modal pre-filled with the leads that were
 * not dialed yet and the old session's settings.
 * @returns {JSX.Element|null}
 */
export default function ResumeSessionBanner() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: RESUMABLE_QUERY_KEY,
    queryFn: () => apiGet('/powerdial/sessions/resumable').catch(() => null),
    staleTime: 30_000,
  });
  const r = data?.resumable;
  if (!r) return null;

  const left = r.remainingLeadIds.length;
  const when = r.endedAt ? new Date(r.endedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

  function resume() {
    usePowerDialDefaults.getState().setDefaults(r.settings || {});
    usePowerDial.getState().setResumeFrom(r.sessionId);
    usePowerDial.getState().openModal(r.remainingLeadIds);
  }
  async function dismiss() {
    // Close instantly; the server call then makes it stick (and a failure brings it back).
    qc.setQueryData(RESUMABLE_QUERY_KEY, { ok: true, resumable: null });
    try {
      await apiPost(`/powerdial/sessions/${r.sessionId}/dismiss-resume`, {});
    } catch (_) {
      toast.error('Could not dismiss — try again');
    }
    qc.invalidateQueries({ queryKey: RESUMABLE_QUERY_KEY });
  }

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 rounded-full border border-civix-purple/40 bg-[#140b22]/95 px-4 py-2 text-sm text-white shadow-xl shadow-purple-900/30" data-testid="powerdial-resume-banner">
      <PlayCircle className="w-4 h-4 text-civix-purpleSoft shrink-0" />
      <span>
        Unfinished Power Dial session — <b>{left}</b> of {r.total} lead{r.total === 1 ? '' : 's'} left{when ? ` · ended ${when}` : ''}
      </span>
      <button type="button" onClick={resume} className="rounded-full bg-civix-purple px-3 py-1 text-xs font-semibold hover:bg-violet-500">
        Resume where I left off
      </button>
      <button type="button" onClick={dismiss} aria-label="Dismiss" className="p-1 text-white/50 hover:text-white">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
